import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Column<T> {
  key: string
  header: string
  render?: (row: T) => React.ReactNode
  className?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  page?: number
  totalPages?: number
  total?: number
  onPageChange?: (page: number) => void
  onRowClick?: (row: T) => void
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  loading,
  page = 1,
  totalPages = 1,
  total,
  onPageChange,
  onRowClick,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-14 animate-pulse border-b"
            style={{
              backgroundColor: i % 2 === 0 ? 'var(--color-surface)' : 'var(--color-gray-50)',
              borderColor: 'var(--color-border)',
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${col.className ?? ''}`}
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={i}
                className={`border-t transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-50)')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <td key={col.key} className={`px-4 py-3 ${col.className ?? ''}`}>
                    {col.render ? col.render(row) : (row[col.key] as React.ReactNode) ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {onPageChange && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {total !== undefined ? `${total} resultados` : `Pagina ${page} de ${totalPages}`}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
